import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit, FiTrash2, FiStar } from 'react-icons/fi';
import {
  fetchPlans,
  fetchSubscriptions,
  createPlan,
  updatePlan,
  deletePlan,
  fetchStats,
  resetSuccess,
} from '../../store/slices/subscriptionSlice';
import { DataTable, Pagination, Modal, ConfirmDialog } from '../../components/Common';
import { formatDate, formatCurrency } from '../../utils/helpers';

const Subscriptions = () => {
  const dispatch = useDispatch();
  const { plans, subscriptions, stats, totalSubscriptions, totalPages, isLoading, success, error } =
    useSelector((state) => state.subscription);

  const [activeTab, setActiveTab] = useState('plans');
  const [currentPage, setCurrentPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price: 0,
    duration: 30,
    features: {
      unlimitedSwipes: false,
      dailySwipeLimit: 10,
      unlimitedChat: false,
      canConnect: false,
      canSeeWhoLikedYou: false,
      profileBoost: 1,
      premiumBadge: false,
    },
    isPopular: false,
    sortOrder: 0,
  });

  useEffect(() => {
    dispatch(fetchPlans());
    dispatch(fetchStats());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      dispatch(fetchSubscriptions({ page: currentPage, limit: 10 }));
    }
  }, [dispatch, activeTab, currentPage]);

  useEffect(() => {
    if (success) {
      toast.success('Operation successful');
      dispatch(resetSuccess());
      setIsModalOpen(false);
      setSelectedPlan(null);
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleOpenModal = (plan = null) => {
    if (plan) {
      setSelectedPlan(plan);
      setFormData({
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        price: plan.price,
        duration: plan.duration,
        features: plan.features || {},
        isPopular: plan.isPopular,
        sortOrder: plan.sortOrder,
      });
    } else {
      setSelectedPlan(null);
      setFormData({
        name: '',
        slug: '',
        description: '',
        price: 0,
        duration: 30,
        features: {
          unlimitedSwipes: false,
          dailySwipeLimit: 10,
          unlimitedChat: false,
          canConnect: false,
          canSeeWhoLikedYou: false,
          profileBoost: 1,
          premiumBadge: false,
        },
        isPopular: false,
        sortOrder: 0,
      });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedPlan) {
      dispatch(updatePlan({ id: selectedPlan._id, data: formData }));
    } else {
      dispatch(createPlan(formData));
    }
  };

  const handleDelete = () => {
    if (selectedPlan) {
      dispatch(deletePlan(selectedPlan._id));
      setIsDeleteOpen(false);
      setSelectedPlan(null);
    }
  };

  const planColumns = [
    { header: 'Name', render: (row) => row.name },
    { header: 'Price', render: (row) => formatCurrency(row.price) },
    { header: 'Duration', render: (row) => `${row.duration} days` },
    {
      header: 'Popular',
      render: (row) => row.isPopular ? <FiStar color="#f59e0b" /> : '-',
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge ${row.isActive ? 'badge-success' : 'badge-secondary'}`}>
          {row.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      header: 'Actions',
      render: (row) => (
        <div className="table-actions">
          <button className="btn btn-secondary btn-icon" onClick={() => handleOpenModal(row)}>
            <FiEdit />
          </button>
          <button
            className="btn btn-danger btn-icon"
            onClick={() => {
              setSelectedPlan(row);
              setIsDeleteOpen(true);
            }}
          >
            <FiTrash2 />
          </button>
        </div>
      ),
    },
  ];

  const subscriptionColumns = [
    {
      header: 'User',
      render: (row) => row.userId?.fullName || row.userId?.mobile || 'N/A',
    },
    { header: 'Plan', render: (row) => row.planId?.name || 'N/A' },
    { header: 'Amount', render: (row) => formatCurrency(row.amount) },
    {
      header: 'Status',
      render: (row) => (
        <span className={`badge badge-${row.status === 'active' ? 'success' : 'secondary'}`}>
          {row.status}
        </span>
      ),
    },
    { header: 'Start Date', render: (row) => formatDate(row.startDate) },
    { header: 'End Date', render: (row) => formatDate(row.endDate) },
  ];

  return (
    <div>
      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
          <div className="card">
            <div className="card-body">
              <h4>{stats.totalSubscriptions || 0}</h4>
              <p>Total Subscriptions</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4>{stats.activeSubscriptions || 0}</h4>
              <p>Active Subscriptions</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4>{formatCurrency(stats.totalRevenue || 0)}</h4>
              <p>Total Revenue</p>
            </div>
          </div>
          <div className="card">
            <div className="card-body">
              <h4>{plans.length}</h4>
              <p>Plans</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: '16px' }}>
        <button
          className={`btn ${activeTab === 'plans' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('plans')}
        >
          Plans
        </button>
        <button
          className={`btn ${activeTab === 'subscriptions' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('subscriptions')}
          style={{ marginLeft: '8px' }}
        >
          Subscriptions
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">
            {activeTab === 'plans' ? 'Subscription Plans' : `Subscriptions (${totalSubscriptions})`}
          </h3>
          {activeTab === 'plans' && (
            <button className="btn btn-primary" onClick={() => handleOpenModal()}>
              <FiPlus /> Add Plan
            </button>
          )}
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {activeTab === 'plans' ? (
            <DataTable columns={planColumns} data={plans} isLoading={isLoading} />
          ) : (
            <>
              <DataTable columns={subscriptionColumns} data={subscriptions} isLoading={isLoading} />
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </>
          )}
        </div>
      </div>

      {/* Plan Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedPlan ? 'Edit Plan' : 'Add Plan'}>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              className="form-control"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label>Slug</label>
            <input
              type="text"
              className="form-control"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
              required
            />
          </div>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <div className="form-group">
              <label>Price (INR)</label>
              <input
                type="number"
                className="form-control"
                value={formData.price}
                onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) })}
              />
            </div>
            <div className="form-group">
              <label>Duration (days)</label>
              <input
                type="number"
                className="form-control"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              />
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              className="form-control"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="form-group">
            <label>Features</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {Object.entries(formData.features).map(([key, value]) => (
                <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {typeof value === 'boolean' ? (
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: { ...formData.features, [key]: e.target.checked },
                        })
                      }
                    />
                  ) : (
                    <input
                      type="number"
                      style={{ width: '60px' }}
                      value={value}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          features: { ...formData.features, [key]: parseInt(e.target.value) },
                        })
                      }
                    />
                  )}
                  <span style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={formData.isPopular}
                onChange={(e) => setFormData({ ...formData, isPopular: e.target.checked })}
              />
              Mark as Popular
            </label>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={() => setIsModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDelete}
        title="Delete Plan"
        message={`Are you sure you want to delete "${selectedPlan?.name}"?`}
        isLoading={isLoading}
      />
    </div>
  );
};

export default Subscriptions;
